import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "src/helpers";
import PulseLoader from "../custom/PulseLoader";

const buttonVariants = cva(
  "inline-flex items-center  justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-btn-primary text-primary-foreground hover:bg-btn-primary-hover ",
        theamBlack: "bg-theamblack text-white hover:bg-theamblack-foreground hover:text-white",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-primary text-primary bg-orange-50 shadow-sm hover:bg-btn-primary-hover hover:text-white",
        outlinePrimary:
          "border border-primary bg-transparent text-primary shadow-sm hover:bg-btn-primary-hover hover:text-white",
        outlineDestructive:
          "border border-destructive text-destructive bg-destructive-50 shadow-sm hover:bg-destructive hover:text-white",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        ghostPrimary: "text-primary hover:bg-orange-100 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        iconBtn: " border-none hover:text-primary",
        primaryIconBtn: "text-primary border-none hover:text-primary   ",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        xs: "h-7 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const loaderColor: any = {
  default: "btn-primary",
  destructive: "bg-white",
  outline: "btn-primary",
  outlinePrimary: "btn-primary",
  outlineDestructive: "bg-destructive",
  secondary: "bg-gray-600",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        disabled={props.disabled || isLoading}
        className={cn(buttonVariants({ variant, size, className }), "")}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <PulseLoader color={loaderColor[variant ? variant : ("default" as keyof object)]} />
        ) : (
          props.children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
